import { Button, Form, FormValues, Space, Typography } from 'antd';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import SingleInputForm from '../../../../shared/components/single-input-form/single-input-form';
import { useHasPermission } from '../../../../shared/hooks/use-has-permission';
import { CheckIcon } from '../../../../shared/icons/check-icon';
import { DashIcon } from '../../../../shared/icons/dash-icon';
import { PencilSquareIcon } from '../../../../shared/icons/pencil-square-icon';
import { XIcon } from '../../../../shared/icons/x-icon';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useUpdateMetadataMutation } from '../../../archive/api/archive-api';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { SignRequestDocumentMetadata } from '../../models/sign-request-document-metadata';
import styles from './sign-request-metadata-item-form.module.css';

interface Props {
  metadata: SignRequestDocumentMetadata;
  documentId?: string;
}

const SignRequestMetadataItemForm = ({ metadata, documentId }: Props): JSX.Element => {
  const [form] = Form.useForm<{ value: string }>();
  const [updateMetadata, { isLoading: isSubmittingMetadata }] = useUpdateMetadataMutation();
  const { t } = useTranslation('common');
  const [editable, setEditable] = useState<boolean>(false);
  const hasPermissionUpdateMetadata = useHasPermission(UserPermissionType.UpdateMetadata);
  const [expanded, setExpanded] = useState(false);

  const initialValues: FormValues<typeof form> = useMemo(
    () => ({
      value: metadata.value,
    }),
    [metadata],
  );

  const handleFinish = (values: FormValues<typeof form>) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    updateMetadata({ id: documentId!, name: metadata.name, value: values.value })
      .unwrap()
      .then(() => setEditable(false))
      .catch(error => {
        setEditable(true);
        handleServerFormErrors(form, error.data?.errors || { value: [t('errors.unexpected')] });
      });
  };

  return (
    <>
      <div className={clsx(editable && styles.dataTextWrapper)}>
        <Typography.Text className={styles.metadataTitle}>{metadata.title}:</Typography.Text>
        <Space align="start">
          <Typography.Paragraph
            className={styles.data}
            ellipsis={{
              rows: 2,
              expandable: 'collapsible',
              expanded,
              onExpand: (_, info) => setExpanded(info.expanded),
            }}
          >
            {metadata.type === 'checkbox' ? (
              metadata.value === '1' ? (
                <>
                  <CheckIcon /> {t('label.options.true')}
                </>
              ) : metadata.value === '0' ? (
                <>
                  <XIcon /> {t('label.options.false')}
                </>
              ) : (
                <DashIcon />
              )
            ) : (
              form.getFieldValue('value') || metadata.value || '-'
            )}
          </Typography.Paragraph>
          {metadata.editable && hasPermissionUpdateMetadata && (
            <Button
              onClick={() => setEditable(true)}
              type="text"
              size="small"
              shape="circle"
              icon={
                <IconWrapper>
                  <PencilSquareIcon className={styles.squareIcon} />
                </IconWrapper>
              }
            />
          )}
        </Space>
      </div>
      <div className={clsx(!editable && styles.dataFormWrapper)}>
        <SingleInputForm
          form={form}
          isSubmitting={isSubmittingMetadata}
          label={metadata.title}
          onFinish={handleFinish}
          initialValues={initialValues}
          onCancel={() => setEditable(false)}
        />
      </div>
    </>
  );
};

export default SignRequestMetadataItemForm;
